import {ChangeDetectorRef, inject, Injectable, OnInit, signal} from '@angular/core';
import {NgForm} from '@angular/forms';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {finalize, map} from 'rxjs';
import {CurrentUserContextService} from '../../../shared/services/current-user-context.service';
import {BusinessLocationModalComponent} from './business-location-modal';
import {
  BusinessLocationSelection,
  GdxCooperativeRegistryDto,
  formatSmceRegistrationNumber,
  normalizeSmceRegistrationNumber,
  SMCE_REGISTRATION_NUMBER_PATTERN
} from './business.models';
import {BusinessApiService, BusinessCertificateOptionDto} from './business-api.service';
import {firstNonBlank} from '../../../shared/commons/text-utils';
import {formatProductionSite} from '../../../shared/commons/certificate-select/production-site';
import {UserBusinessApiService} from '../user-business-api.service';
import {ToastService} from '../../../shared/commons/toast/toast-service';
import {ConfirmDialogService} from '../../../shared/commons/dialog/confirm-dialog/confirm-dialog.service';
import {
  BusinessTypeStageGroup,
  groupBusinessTypesByStage,
  isBusinessTypeActive,
  selectedTypeTitles,
  summarizeTitles
} from '../../../shared/commons/business-type/business-type-stage';
import {
  BusinessCertificateRefDto,
  BusinessJuristicTypeDto,
  BusinessTypeDto,
  GdxJuristicAddress,
  GdxSmceProfileResponse,
  UserAddressOptionDto,
  UserBusinessDto,
  CertificateItemDto,
  ProductionSiteDto
} from '../../../../models/generated-models';

export type BusinessType = 'individual' | 'company' | 'cooperative';
export type CooperativeType = 7 | 8;

@Injectable()
export abstract class AddBusinessFormData implements OnInit {
  protected readonly modalService = inject(NgbModal);
  protected readonly businessApiService = inject(BusinessApiService);
  protected readonly userBusinessApiService = inject(UserBusinessApiService);
  protected readonly toast = inject(ToastService);
  protected readonly confirmDialog = inject(ConfirmDialogService);
  protected readonly cdr = inject(ChangeDetectorRef);
  protected readonly currentUserContext = inject(CurrentUserContextService);

  editMode = false;
  initialBusiness: UserBusinessDto | null = null;
  isSubmitting = false;
  isConfirming = false;
  isSearchingCooperative = false;
  saveError = undefined;
  businessType = signal<BusinessType>('individual');
  cooperativeType = signal<CooperativeType>(7);
  cooperativeTypeOptions: Array<{ value: CooperativeType; label: string }> = [
    {value: 7, label: 'สหกรณ์'},
    {value: 8, label: 'วิสาหกิจชุมชน'}
  ];
  /**
   * Options for the ประเภทธุรกิจ checkboxes (GET /api/businesses/business-types?includeInactive=true):
   * switched-off types are loaded too so a business that already has one keeps showing it.
   */
  businessTypeOptions: BusinessTypeDto[] = [];
  certificateOptions: BusinessCertificateOptionDto[] = [];
  individualManualAddress = signal(false);
  companyManualAddress = signal(false);
  cooperativeManualAddress = signal(false);
  cooperativeSearchKeyword = undefined;
  cooperativeData: any = null;
  isSearchingCompany = false;
  selectedJuristicType: BusinessJuristicTypeDto | null = null;

  // Template-driven model bound directly via [(ngModel)]
  model: UserBusinessDto = this.emptyModel();
  // UI-only fields not on UserBusinessDto
  /** A business can carry many ประเภทธุรกิจ — sent as UserBusinessDto.businessTypeIds. */
  selectedBusinessTypeIds: number[] = [];
  selectedCertificateKeys: string[] = [];
  subDistrict: UserAddressOptionDto | null = null;
  districtName = undefined;
  provinceName = undefined;
  isActive = true;

  private initialBusinessApplied = false;

  /**
   * The only way to switch the active form. Wipes every field that is
   * type-specific so no value from the previous form can leak into the next
   * one. `reset` is skipped when the caller populates the model itself right
   * after the switch (edit-mode initialization).
   */
  setBusinessType(type: BusinessType, reset = true): void {
    if (reset && this.businessType() !== type) {
      this.resetFormState();
    }
    this.businessType.set(type);
    this.lockIndividualTaxId();
  }

  /** The person behind an individual (บุคคลธรรมดา) business — shown above the form. */
  get individualOwnerName(): string {
    const profile = this.currentUserContext.profile?.();
    const full = `${profile?.fullName ?? ''}`.trim();
    if (full) {
      return full;
    }
    return [profile?.prefixName, profile?.firstName, profile?.lastName]
      .map((part) => `${part ?? ''}`.trim())
      .filter((part) => !!part)
      .join(' ');
  }

  /** An individual business is the person themself: its tax id is their card number, fixed. */
  get individualTaxIdLocked(): boolean {
    return this.businessType() === 'individual' && !!`${this.currentUserContext.profile?.()?.cardNo ?? ''}`.trim();
  }

  private lockIndividualTaxId(): void {
    if (this.businessType() !== 'individual') {
      return;
    }
    const cardNo = `${this.currentUserContext.profile?.()?.cardNo ?? ''}`.trim();
    if (cardNo) {
      this.model.taxId = cardNo;
    }
  }

  private resetFormState(): void {
    this.model = this.emptyModel();
    this.selectedBusinessTypeIds = [];
    this.selectedCertificateKeys = [];
    this.appliedSiteKey = null;
    this.subDistrict = null;
    this.districtName = undefined;
    this.provinceName = undefined;
    this.isActive = true;
    this.saveError = undefined;
    // cooperative
    this.cooperativeType.set(7);
    this.cooperativeSearchKeyword = undefined;
    this.cooperativeData = null;
    // company
    this.selectedJuristicType = null;
    this.individualManualAddress.set(false);
    this.companyManualAddress.set(false);
    this.cooperativeManualAddress.set(false);
  }

  protected abstract handleSubmitSuccess(saved: UserBusinessDto): void;

  ngOnInit(): void {
    this.businessApiService.getBusinessTypes(true).subscribe({
      next: (items) => {
        const options = (items ?? []).filter((item) => item?.id != null && !!`${item.title ?? ''}`.trim());
        if (options.length > 0) {
          this.businessTypeOptions = options;
        }
        this.applyInitialBusiness();
        this.cdr.markForCheck();
      },
      error: () => undefined
    });
    this.loadCertificateOptions();
    this.applyInitialBusiness();
    this.lockIndividualTaxId();
  }

  private emptyModel(): UserBusinessDto {
    return {
      businessFormat: 1,
      state: 1,
      certificateNos: [],
      certificates: []
    };
  }

  isBusinessTypeSelected(id: number | null | undefined): boolean {
    return id != null && this.selectedBusinessTypeIds.includes(id);
  }

  /** The checkboxes grouped by หมวด (ต้นน้ำ / กลางน้ำ / ปลายน้ำ), work item 198. */
  get businessTypeGroups(): BusinessTypeStageGroup[] {
    return groupBusinessTypesByStage(this.businessTypeOptions, this.selectedBusinessTypeIds);
  }

  isBusinessTypeActive(type: BusinessTypeDto): boolean {
    return isBusinessTypeActive(type);
  }

  /**
   * Checkbox handler for the ประเภทธุรกิจ list. Always writes a NEW array so the
   * hidden `businessTypeIds` ngModel sees a changed reference and re-runs its
   * required validation (the zoneless app never diffs array contents).
   */
  toggleBusinessType(id: number | null | undefined, checked: boolean): void {
    if (id == null) {
      return;
    }
    const next = this.selectedBusinessTypeIds.filter((item) => item !== id);
    this.selectedBusinessTypeIds = checked ? [...next, id] : next;
    this.cdr.markForCheck();
  }

  setCooperativeType(type: CooperativeType): void {
    this.cooperativeType.set(type);
    this.cooperativeData = null;
    this.cooperativeSearchKeyword = undefined;
    this.model.juristicType = type;
  }

  searchCooperative(): void {
    if (this.isSearchingCooperative) {
      return;
    }
    const keyword = `${this.cooperativeSearchKeyword || (this.model.taxId ?? '')}`.trim();
    if (!keyword) {
      this.cooperativeData = null;
      return;
    }

    if (this.cooperativeType() === 8) {
      this.searchSmce(keyword);
      return;
    }

    this.isSearchingCooperative = true;
    // Guard against a form reset (type switch) while this lookup is in flight.
    const activeModel = this.model;
    this.businessApiService.findCooperativeRegistry(keyword)
      .pipe(finalize(() => {
        this.isSearchingCooperative = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: (registry) => {
          if (this.model !== activeModel) {
            return;
          }
          const registryNo = registry?.registryNo?.trim();
          const registryNo13 = registry?.registryNo13?.trim();
          const cooperativeName = registry?.cooperativeName?.trim();
          if (!registryNo && !registryNo13 && !cooperativeName) {
            this.cooperativeData = null;
            this.toast.warning('ไม่พบข้อมูลสหกรณ์ที่ค้นหา');
            return;
          }
          this.cooperativeData = {
            registrationNumber: registryNo ?? registryNo13 ?? keyword,
            registrationNumber13: registryNo13 ?? registryNo ?? keyword,
            cooperativeName: cooperativeName ?? ''
          };
          this.model.taxId = registryNo13 ?? registryNo ?? keyword;
          this.model.juristicName = cooperativeName ?? null;
          this.model.title = cooperativeName ?? this.model.title;
          this.applyCooperativeAddress(registry);
        },
        error: () => {
          if (this.model !== activeModel) {
            return;
          }
          this.cooperativeData = null;
        }
      });
  }

  /** Mockup SMCE search — same lookup as the normal one but via the endpoint
   *  that does not enforce the signing-authority role check. */
  searchSmceMock(): void {
    const keyword = `${this.cooperativeSearchKeyword || (this.model.taxId ?? '')}`.trim();
    if (!keyword) {
      this.cooperativeData = null;
      return;
    }
    this.searchSmce(keyword, true);
  }

  onSmceRegistrationInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = formatSmceRegistrationNumber(input.value);
    this.cooperativeSearchKeyword = formatted;
    if (input.value !== formatted) {
      input.value = formatted;
    }
  }

  private searchSmce(keyword: string, bypassRoleCheck = false): void {
    const formattedNumber = formatSmceRegistrationNumber(keyword);
    const registrationNumber = normalizeSmceRegistrationNumber(formattedNumber);
    this.cooperativeSearchKeyword = formattedNumber;
    if (!SMCE_REGISTRATION_NUMBER_PATTERN.test(formattedNumber)) {
      this.cooperativeData = null;
      this.toast.warning('กรุณาระบุเลขทะเบียนวิสาหกิจชุมชนในรูปแบบ x-xx-xx-xx/x-xxxx');
      return;
    }

    this.isSearchingCooperative = true;
    // Guard against a form reset (type switch) while this lookup is in flight.
    const activeModel = this.model;
    // The mockup search reads the same persistent SMCE data but skips the
    // signing-authority role check in the /own/smce controller.
    const profile$ = bypassRoleCheck
      ? this.businessApiService.findSmceProfile(registrationNumber)
      : this.businessApiService.findOwnSmceProfile(registrationNumber);
    profile$
      .pipe(finalize(() => {
        this.isSearchingCooperative = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: (profile) => {
          if (this.model !== activeModel) {
            return;
          }
          const profileId = normalizeSmceRegistrationNumber(profile?.id);
          const resolvedNumber = profileId || registrationNumber;
          const cooperativeName = profile?.name?.trim();
          if (!profileId && !cooperativeName) {
            this.cooperativeData = null;
            this.toast.warning('ไม่พบข้อมูลวิสาหกิจชุมชนที่ค้นหา');
            return;
          }

          this.cooperativeSearchKeyword = formatSmceRegistrationNumber(resolvedNumber);
          this.cooperativeData = {
            registrationNumber: this.cooperativeSearchKeyword,
            registrationNumber13: resolvedNumber,
            cooperativeName: cooperativeName ?? ''
          };
          this.model.taxId = resolvedNumber;
          this.model.juristicName = cooperativeName ?? null;
          this.model.title = cooperativeName ?? this.model.title;
          this.applySmceProfile(profile);
        },
        error: (err) => {
          if (this.model !== activeModel) {
            return;
          }
          this.cooperativeData = null;
          const message = err?.error?.message;
          if (message) {
            this.toast.warning(message);
          } else if (err?.status === 404) {
            this.toast.warning('ไม่พบข้อมูลวิสาหกิจชุมชนที่ค้นหา');
          }
        }
      });
  }

  private applySmceProfile(profile: GdxSmceProfileResponse): void {
    const address = profile?.address;
    this.model.hqPhone = this.trimOrNull(profile?.phone);
    this.model.hqMobile = this.model.hqPhone;
    this.model.email = this.trimOrNull(profile?.primaryEmail);
    this.model.hqAddressNo = this.smceAddressNo(address?.fullAddress);
    this.model.hqSoi = null;
    this.model.hqStreet = null;
    this.model.hqSubdistrictId = address?.subdistrictId ?? null;
    this.model.hqDistrictId = address?.districtId ?? null;
    this.model.hqProvinceId = address?.provinceId ?? null;
    this.model.hqZipcode = this.trimOrNull(address?.postCode);
    this.model.latitude = this.coordinateOrNull(profile?.latitude);
    this.model.longitude = this.coordinateOrNull(profile?.longitude);
    this.model.juristicRegDate = this.dateOrNull(profile?.established);
    this.model.detail = this.smceActivityDetail(profile?.bussiness);
    this.resolveSmceSubdistrict(address);
  }

  private resolveSmceSubdistrict(address: GdxSmceProfileResponse['address']): void {
    this.subDistrict = null;
    this.districtName = undefined;
    this.provinceName = undefined;
    const subdistrictId = address?.subdistrictId;
    const addressDetails = this.parseSmceAddress(address?.fullAddress);
    if (subdistrictId == null && !addressDetails) {
      return;
    }

    const addressRequest = addressDetails && subdistrictId != null
      ? this.businessApiService.getAddressBySubdistrictCode(`${subdistrictId}`, addressDetails)
      : subdistrictId != null
        ? this.businessApiService.getAddressBySubdistrictId(subdistrictId, address?.postCode)
        : this.businessApiService.searchAddresses(addressDetails?.subdistrict ?? '', 10).pipe(
          map((options) => options.find((option) =>
            this.sameAddressPart(option.subdistrictTitle, addressDetails?.subdistrict)
            && this.sameAddressPart(option.districtTitle, addressDetails?.district)
            && this.sameAddressPart(option.provinceTitle, addressDetails?.province)
            && (!addressDetails?.zipcode || option.zipcode === addressDetails.zipcode)
          ) ?? null)
        );

    addressRequest.subscribe({
      next: (option) => {
        if (!option) {
          this.cdr.detectChanges();
          return;
        }
        this.subDistrict = option;
        this.districtName = option?.districtTitle ?? '';
        this.provinceName = option?.provinceTitle ?? '';
        this.model.hqSubdistrictId = option?.subdistrictId ?? address?.subdistrictId ?? null;
        this.model.hqDistrictId = option?.districtId ?? address?.districtId ?? null;
        this.model.hqProvinceId = option?.provinceId ?? address?.provinceId ?? null;
        this.model.hqZipcode = option?.zipcode ?? this.trimOrNull(address?.postCode);
        this.cdr.detectChanges();
      },
      error: () => {
        this.cdr.detectChanges();
      }
    });
  }

  private parseSmceAddress(value: string | null | undefined): {
    subdistrict: string;
    district: string;
    province: string;
    zipcode: string;
  } | null {
    const address = `${value ?? ''}`.replace(/\s+/g, ' ').trim();
    const match = address.match(
      /(?:ตำบล|ต\.|แขวง)\s*([^\s]+).*?(?:อำเภอ|อ\.|เขต)\s*([^\s]+)\s+(?:จังหวัด|จ\.)?\s*([^\s]+?)(?:\s+(\d{4,5}))?\s*$/
    );
    if (!match) {
      return null;
    }
    return {
      subdistrict: match[1],
      district: match[2],
      province: match[3],
      zipcode: match[4] ?? ''
    };
  }

  private smceAddressNo(value: string | null | undefined): string | null {
    const address = `${value ?? ''}`.replace(/\s+/g, ' ').trim();
    const match = address.match(/(?:บ้านเลขที่|เลขที่)\s*([0-9]+(?:\s*\/\s*[0-9]+)?)/)
      ?? address.match(/^([0-9]+(?:\s*\/\s*[0-9]+)?)/);
    return this.trimOrNull(match?.[1]?.replace(/\s*\/\s*/g, '/'));
  }

  private sameAddressPart(actual: string | null | undefined, expected: string | null | undefined): boolean {
    return `${actual ?? ''}`.trim().toLocaleLowerCase() === `${expected ?? ''}`.trim().toLocaleLowerCase();
  }

  private smceActivityDetail(activities: GdxSmceProfileResponse['bussiness']): string | null {
    const names = [
      ...new Set(
        (activities ?? [])
          .map((smceActivity) => this.trimOrNull(smceActivity?.name ?? smceActivity?.productDescription))
          .filter((name): name is string => !!name)
      )
    ];
    return names.length > 0 ? names.map((name) => `- ${name}`).join('\n') : null;
  }

  private trimOrNull(value: string | null | undefined): string | null {
    const normalized = `${value ?? ''}`.trim();
    return normalized || null;
  }

  private coordinateOrNull(value: number | null | undefined): string | null {
    return value == null ? null : `${value}`;
  }

  private dateOrNull(value: Date | string | null | undefined): string | null {
    if (value == null) {
      return null;
    }
    if (value instanceof Date) {
      return value.toISOString().slice(0, 10);
    }
    return this.trimOrNull(value)?.slice(0, 10) ?? null;
  }

  /** Recursively finds the first non-empty text value for a TGIX key (mirrors the backend's JsonNode.findPath). */
  private findGdxText(node: unknown, key: string): string | null {
    if (node == null || typeof node !== 'object') {
      return null;
    }
    const record = node as Record<string, unknown>;
    if (key in record) {
      const value = record[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    for (const [childKey, childValue] of Object.entries(record)) {
      if (childKey === key || childValue == null || typeof childValue !== 'object') {
        continue;
      }
      const found = this.findGdxText(childValue, key);
      if (found) {
        return found;
      }
    }
    return null;
  }

  /**
   * Fills the address section from the mapped CPD cooperative profile.
   * CPD returns the administrative divisions directly (subdistrict / district /
   * province) plus the house number embedded in cd:AddressTextTH.
   */
  private applyCooperativeAddress(registry: GdxCooperativeRegistryDto): void {
    const address = registry?.address;
    const postcode = this.trimOrNull(registry?.postcode);
    const parsedText = this.parseCooperativeAddressText(registry?.addressText);
    this.model.hqAddressNo = parsedText?.addressNo ?? this.model.hqAddressNo ?? null;
    this.model.hqSoi = parsedText?.soi ?? this.model.hqSoi ?? null;
    this.model.hqStreet = parsedText?.street ?? this.model.hqStreet ?? null;
    if (postcode) {
      this.model.hqZipcode = postcode;
    }
    this.resolveCooperativeSubdistrict({
      subdistrict: this.trimOrNull(address?.subdistrict) ?? '',
      district: this.trimOrNull(address?.district) ?? '',
      province: this.trimOrNull(address?.province) ?? '',
      zipcode: postcode ?? ''
    });
  }

  /**
   * Extracts the house number / soi / street from the CPD address text line.
   * The house number is the leading number (optionally after เลขที่/บ้าน/ซ.), which
   * keeps it distinct from the following มู่ท่ี (moo) number.
   */
  private parseCooperativeAddressText(value: string | null | undefined): {
    addressNo: string | null;
    soi: string | null;
    street: string | null;
  } {
    const text = this.trimOrNull(value);
    if (!text) {
      return {addressNo: null, soi: null, street: null};
    }
    const number = text.match(/^(?:เลขท่ี|เลขท่ี|บ้าน|บ\.|ซอย|ซ\.)?\s*(\d+(?:\s*\/\s*\d+)?)/)?.[1];
    const addressNo = this.trimOrNull(number?.replace(/\s*\/\s*/g, '/'));
    const soi = this.trimOrNull(text.match(/(?:ซอย|ซ\.)\s*([^\s]+(?:\s+[^\s]+)?)/)?.[1]);
    const street = this.trimOrNull(text.match(/(?:ถนน|ถ\.)\s*([^\s]+(?:\s+[^\s]+)?)/)?.[1]);
    return {addressNo, soi, street};
  }

  private resolveCooperativeSubdistrict(details: {
    subdistrict: string;
    district: string;
    province: string;
    zipcode: string;
  }): void {
    if (!details.subdistrict) {
      return;
    }
    this.subDistrict = null;
    this.districtName = undefined;
    this.provinceName = undefined;
    this.businessApiService.searchAddresses(details.subdistrict, 10).subscribe({
      next: (options) => {
        const option = options.find((candidate) =>
          this.sameAddressPart(candidate.subdistrictTitle, details.subdistrict)
          && this.sameAddressPart(candidate.districtTitle, details.district)
          && this.sameAddressPart(candidate.provinceTitle, details.province)
          && (!details.zipcode || candidate.zipcode === details.zipcode)
        ) ?? null;
        if (!option) {
          this.cdr.detectChanges();
          return;
        }
        this.subDistrict = option;
        this.districtName = option.districtTitle ?? '';
        this.provinceName = option.provinceTitle ?? '';
        this.model.hqSubdistrictId = option.subdistrictId ?? null;
        this.model.hqDistrictId = option.districtId ?? null;
        this.model.hqProvinceId = option.provinceId ?? null;
        this.model.hqZipcode = option.zipcode ?? this.model.hqZipcode ?? null;
        this.cdr.detectChanges();
      },
      error: () => {
        this.cdr.detectChanges();
      }
    });
  }

  onSubDistrictChange(option: UserAddressOptionDto | null): void {
    this.subDistrict = option;
    this.districtName = option?.districtTitle ?? '';
    this.provinceName = option?.provinceTitle ?? '';
    const zip = option?.zipcode ?? null;
    if (this.businessType() === 'individual') {
      this.model.subdistrictId = option?.subdistrictId ?? null;
      this.model.districtId = option?.districtId ?? null;
      this.model.provinceId = option?.provinceId ?? null;
      this.model.zipcode = zip;
    } else {
      this.model.hqSubdistrictId = option?.subdistrictId ?? null;
      this.model.hqDistrictId = option?.districtId ?? null;
      this.model.hqProvinceId = option?.provinceId ?? null;
      this.model.hqZipcode = zip;
    }
  }

  openLocationPicker(type: BusinessType): void {
    const modalRef = this.modalService.open(BusinessLocationModalComponent, {
      size: 'xl',
      centered: true,
      backdrop: 'static'
    });

    modalRef.componentInstance.latitude = this.model.latitude ?? null;
    modalRef.componentInstance.longitude = this.model.longitude ?? null;
    modalRef.result
      .then((selection: BusinessLocationSelection | undefined) => {
        if (!selection) {
          return;
        }
        // NgbModal resolves its result while closing its view. Update the
        // form in the next task so Angular does not re-check a changed ngModel
        // binding in IndividualFormComponent during that close operation.
        setTimeout(() => {
          this.model.latitude = selection.latitude;
          this.model.longitude = selection.longitude;
          this.cdr.markForCheck();
        });
      })
      .catch(() => undefined);
  }

  searchCompany(): void {
    if (this.isSearchingCompany) {
      return;
    }
    const taxId = `${this.model.taxId ?? ''}`.trim();
    if (!taxId) {
      this.clearCompanyLookupFields();
      return;
    }

    this.isSearchingCompany = true;
    // Capture the model this search started against. If the form is reset
    // (e.g. the user switches business type) the in-flight lookup would
    // otherwise write stale company fields into the fresh model.
    const activeModel = this.model;
    this.businessApiService.findOwnJuristicProfile(taxId)
      .pipe(finalize(() => {
        this.isSearchingCompany = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: (profile) => {
          if (this.model !== activeModel) {
            return;
          }
          const juristicId = profile?.juristicId?.trim();
          const juristicType = profile?.juristicType?.trim();
          const nameTh = profile?.nameTh?.trim();
          const nameEn = profile?.nameEn?.trim();
          const address = profile?.juristicAddress?.addressNo?.trim();
          const soi = profile?.soi?.trim();
          const road = profile?.road?.trim();
          const juristicRegDate = profile?.registerDate?.trim();
          if (!juristicId && !nameTh) {
            this.clearCompanyLookupFields();
            this.toast.warning('ไม่พบเลขทะเบียนนิติบุคคลที่ค้นหา');
            return;
          }
          this.model.taxId = juristicId ?? taxId;
          if (juristicType) {
            this.resolveJuristicType(juristicType);
          } else {
            this.model.juristicType = undefined;
          }
          this.model.title = nameTh ?? this.model.title;
          this.model.titleEn = nameEn ?? this.model.titleEn;
          this.model.juristicName = nameTh;
          this.model.juristicRegDate = juristicRegDate;
          this.model.juristicAddress = address;
          this.model.juristicAuthorizedSignatory = profile?.signingAuthorityDetail?.trim();
          this.model.juristicDirectors = profile?.directorNames?.trim();
          this.model.hqAddressNo = address;
          this.model.hqSoi = soi;
          this.model.hqStreet = road;
          this.resolveJuristicSubdistrict(profile?.juristicAddress);
        },
        error: (err) => {
          if (this.model !== activeModel) {
            return;
          }
          this.clearCompanyLookupFields();
          if (err?.status === 404) {
            this.toast.warning('ไม่พบเลขทะเบียนนิติบุคคลที่ค้นหา');
          }
        }
      });
  }

  private clearCompanyLookupFields(): void {
    this.model.juristicName = null;
    this.model.juristicAddress = null;
    this.model.juristicAuthorizedSignatory = null;
    this.model.juristicDirectors = null;
    this.model.hqAddressNo = null;
    this.model.hqSoi = null;
    this.model.hqStreet = null;
    this.clearSubdistrictSelection();
  }

  private clearSubdistrictSelection(): void {
    this.subDistrict = null;
    this.districtName = undefined;
    this.provinceName = undefined;
    this.model.hqSubdistrictId = undefined;
    this.model.hqDistrictId = undefined;
    this.model.hqProvinceId = undefined;
    this.model.hqZipcode = null;
  }

  private resolveJuristicSubdistrict(address: GdxJuristicAddress | undefined): void {
    this.clearSubdistrictSelection();
    const normalizedCode = address?.subdistrictCode?.trim();
    if (!normalizedCode) {
      return;
    }
    // Guard against a form reset (type switch) while this lookup is in flight.
    const activeModel = this.model;
    this.businessApiService.getAddressBySubdistrictCode(normalizedCode, {
      subdistrict: address?.subdistrict,
      district: address?.district,
      province: address?.province,
      zipcode: address?.zipcode
    }).subscribe({
      next: (option) => {
        if (this.model !== activeModel) {
          return;
        }
        this.onSubDistrictChange(option);
        this.cdr.detectChanges();
      },
      error: () => {
        if (this.model !== activeModel) {
          return;
        }
        this.clearSubdistrictSelection();
        this.cdr.detectChanges();
      }
    });
  }

  private resolveJuristicType(juristicTypeName: string): void {
    const name = `${juristicTypeName ?? ''}`.trim();
    if (!name) {
      this.selectedJuristicType = null;
      this.model.juristicType = undefined;
      return;
    }
    // Guard against a form reset (type switch) while this lookup is in flight.
    const activeModel = this.model;
    this.businessApiService.getJuristicTypes()
      .subscribe({
        next: (types) => {
          if (this.model !== activeModel) {
            return;
          }
          const matched = this.findJuristicTypeByName(types, name);
          this.selectedJuristicType = matched;
          this.model.juristicType = matched?.id;
          this.cdr.detectChanges();
        },
        error: () => {
          if (this.model !== activeModel) {
            return;
          }
          this.selectedJuristicType = null;
          this.model.juristicType = null;
          this.cdr.detectChanges();
        }
      });
  }

  private findJuristicTypeByName(types: BusinessJuristicTypeDto[] | null | undefined, name: string): BusinessJuristicTypeDto | undefined {
    const normalized = this.normalizeJuristicTypeName(name);
    if (!normalized) {
      return undefined;
    }
    return (types ?? []).find((item) => this.normalizeJuristicTypeName(`${item.title ?? ''}`) === normalized);
  }

  private normalizeJuristicTypeName(value: string): string {
    return `${value ?? ''}`.trim().replace(/\s+/g, ' ').toUpperCase();
  }

  getCooperativeRegistrationDocumentLabel(): string {
    return this.cooperativeType() === 7 || this.cooperativeType() === 8
      ? 'หลักฐานการจดทะเบียน'
      : 'หลักฐานการจดทะเบียนพาณิชย์';
  }

  /** Selected types set to บังคับใบรับรอง in the เมนูตั้งค่าประเภทธุรกิจ (work item 198). */
  get certificateRequiredBy(): string[] {
    return selectedTypeTitles(this.businessTypeOptions, this.selectedBusinessTypeIds, (type) => !!type.requireCertificate);
  }

  /** At least one certificate is required when a selected type says so; the backend checks the same rule. */
  get certificateRequired(): boolean {
    return this.certificateRequiredBy.length > 0;
  }

  get certificateRequiredNote(): string {
    return summarizeTitles(this.certificateRequiredBy);
  }

  /** Selected types set to บังคับพิกัด in the เมนูตั้งค่าประเภทธุรกิจ (work item 198). */
  get locationRequiredBy(): string[] {
    return selectedTypeTitles(this.businessTypeOptions, this.selectedBusinessTypeIds, (type) => !!type.requireLocation);
  }

  /** A map location is required when a selected type says so; the backend checks the same rule. */
  get locationRequired(): boolean {
    return this.locationRequiredBy.length > 0;
  }

  get locationRequiredNote(): string {
    return summarizeTitles(this.locationRequiredBy);
  }

  get certificateOptional(): boolean {
    return !this.certificateRequired;
  }

  onSubmit(form: NgForm): void {
    form.form.markAllAsTouched();
    form.form.updateValueAndValidity();

    if (form.invalid) {
      const businessTypesInvalid = form.controls?.['businessTypeIds']?.invalid;
      const certInvalid = form.controls?.['certificateNos']?.invalid;
      const locationInvalid = form.controls?.['latitude']?.invalid;
      this.saveError = businessTypesInvalid
        ? 'กรุณาเลือกประเภทธุรกิจอย่างน้อย 1 ประเภท'
        : certInvalid
          ? 'กรุณาเลือกอย่างน้อย 1 ใบรับรอง'
          : locationInvalid
            ? 'กรุณาเลือกตำแหน่งบนแผนที่'
            : 'กรุณากรอกข้อมูลที่จำเป็นให้ครบ';
      return;
    }

    const type = this.businessType();
    const payload = this.buildBackendPayload(type);
    // Payload-level guard for a certificate-required business whose selection emptied after the
    // control last validated; the control may be absent from a form without the section.
    const certificateControl = form.controls?.['certificateNos'];
    if (certificateControl && this.certificateRequired
        && !payload.certificateNos?.length && !payload.certificates?.length) {
      certificateControl.setErrors({...certificateControl.errors, required: true});
      certificateControl.markAsTouched();
      this.saveError = 'กรุณาเลือกอย่างน้อย 1 ใบรับรอง';
      return;
    }

    this.confirmSubmission(payload);
  }

  private confirmSubmission(payload: UserBusinessDto): void {
    this.isConfirming = true;
    this.confirmDialog.open({
      title: this.editMode ? 'ยืนยันการแก้ไขข้อมูลธุรกิจ' : 'ยืนยันการบันทึกข้อมูลธุรกิจ',
      content: this.editMode ? 'คุณต้องการบันทึกการแก้ไขข้อมูลธุรกิจนี้ใช่หรือไม่?' : 'คุณต้องการบันทึกข้อมูลธุรกิจนี้ใช่หรือไม่?',
      confirmButtonMsg: this.editMode ? 'บันทึกการแก้ไข' : 'บันทึกข้อมูล',
      closeButtonMsg: 'ตรวจสอบอีกครั้ง'
    }).subscribe({
      next: (confirmed) => {
        this.isConfirming = false;
        if (confirmed) {
          this.save(payload);
        }
      },
      error: () => {
        this.isConfirming = false;
        this.cdr.markForCheck();
      }
    });
  }

  private save(payload: UserBusinessDto): void {
    this.isSubmitting = true;
    this.saveError = undefined;

    this.userBusinessApiService.createBusiness(payload).subscribe({
      next: (saved) => {
        this.isSubmitting = false;
        this.handleSubmitSuccess(saved);
      },
      error: () => {
        // Error dialogs can start a nested Angular check while the HTTP error
        // is propagating. Reset on the next task so the disabled binding stays
        // stable for the current check and always renders as enabled afterward.
        setTimeout(() => {
          this.isSubmitting = false;
          this.cdr.markForCheck();
        });
      }
    });
  }

  private applyInitialBusiness(): void {
    if (!this.editMode || this.initialBusinessApplied || !this.initialBusiness || this.businessTypeOptions.length === 0) {
      return;
    }
    // Wipe create-mode state before populating, so editing after a previous
    // form visit starts from a clean model instead of leftover values.
    this.resetFormState();

    const business = this.initialBusiness;
    const businessFormat = business.businessFormat ?? 1;
    const businessType: BusinessType = businessFormat === 2
      ? 'company'
      : businessFormat === 3
        ? 'cooperative'
        : 'individual';

    // NOTE: populate model fully BEFORE updating businessType signal.
    // If businessType.set() runs first, the @if re-renders the company block
    // while model.juristicName is still undefined → NG0100 in dev mode.
    this.model = {
      id: business.id,
      businessFormat,
      taxId: business.taxId ?? null,
      title: business.title ?? null,
      titleEn: business.titleEn ?? null,
      detail: business.detail ?? null,
      email: business.email ?? null,
      webSite: business.webSite ?? null,
      businessRegFile: business.businessRegFile ?? null,
      certificateNos: [],
      certificates: business.certificates ?? [],
      latitude: business.latitude ?? null,
      longitude: business.longitude ?? null,
      state: business.state ?? 1
    };
    this.selectedBusinessTypeIds = this.normalizeBusinessTypeIds(business.businessTypeIds);
    this.isActive = (business.state ?? 1) !== 0;
    this.selectedCertificateKeys = this.getInitialCertificateSelectionKeys(business);
    // The saved address wins over the certificate's site until the user changes the selection.
    this.appliedSiteKey = `${this.newestSelectedCertificateWithSite()?.certificateKey ?? ''}`.trim().toUpperCase() || null;

    if (businessType === 'individual') {
      this.individualManualAddress.set(true);
      this.model.savedBy = business.savedBy ?? null;
      this.model.addressNo = business.addressNo ?? null;
      this.model.soi = business.soi ?? null;
      this.model.street = business.street ?? null;
      this.model.subdistrictId = business.subdistrictId ?? null;
      this.model.districtId = business.districtId ?? null;
      this.model.provinceId = business.provinceId ?? null;
      this.model.zipcode = business.zipcode ?? null;
      this.model.phone = business.phone ?? null;
      this.model.fax = business.fax ?? null;
    } else if (businessType === 'company') {
      this.companyManualAddress.set(true);
      this.model.juristicType = business.juristicType ?? null;
      this.model.juristicRegDate = business.juristicRegDate ?? null;
      this.model.juristicName = firstNonBlank(business.juristicName, business.title) || null;
      this.model.juristicAddress = firstNonBlank(business.juristicAddress, business.hqAddressNo, business.addressNo) || null;
      this.model.juristicAuthorizedSignatory = firstNonBlank(business.juristicAuthorizedSignatory, business.savedBy) || null;
      this.model.juristicDirectors = firstNonBlank(business.juristicDirectors, '') || null;
      this.model.hqAddressNo = business.hqAddressNo ?? business.addressNo ?? null;
      this.model.hqSoi = business.hqSoi ?? business.soi ?? null;
      this.model.hqStreet = business.hqStreet ?? business.street ?? null;
      this.model.hqSubdistrictId = business.hqSubdistrictId ?? business.subdistrictId ?? null;
      this.model.hqDistrictId = business.hqDistrictId ?? business.districtId ?? null;
      this.model.hqProvinceId = business.hqProvinceId ?? business.provinceId ?? null;
      this.model.hqZipcode = business.hqZipcode ?? business.zipcode ?? null;
      this.model.hqPhone = firstNonBlank(business.hqPhone, business.phone) || null;
      this.model.hqFax = firstNonBlank(business.hqFax, business.fax) || null;
      this.model.hqMobile = this.model.hqPhone ?? null;
    } else {
      this.cooperativeManualAddress.set(true);
      this.cooperativeType.set(business.juristicType === 8 ? 8 : 7);
      this.cooperativeSearchKeyword = this.cooperativeType() === 8
        ? formatSmceRegistrationNumber(business.taxId)
        : business.taxId ?? undefined;
      this.model.juristicType = this.cooperativeType();
      this.model.juristicName = business.title ?? null;
      this.model.hqAddressNo = business.hqAddressNo ?? business.addressNo ?? null;
      this.model.hqSoi = business.hqSoi ?? business.soi ?? null;
      this.model.hqStreet = business.hqStreet ?? business.street ?? null;
      this.model.hqZipcode = business.hqZipcode ?? business.zipcode ?? null;
      this.model.hqPhone = business.phone ?? business.hqPhone ?? null;
      this.model.hqFax = business.hqFax ?? business.fax ?? null;
      this.model.hqMobile = this.model.hqPhone ?? null;
      const isSmce = this.cooperativeType() === 8;
      const cooperativeRegistrationNumber = isSmce
        ? formatSmceRegistrationNumber(business.taxId)
        : business.taxId ?? '';
      const normalizedRegistrationNumber = isSmce
        ? normalizeSmceRegistrationNumber(business.taxId)
        : business.taxId ?? '';
      if (isSmce) {
        this.model.taxId = normalizedRegistrationNumber;
      }
      this.cooperativeData = {
        registrationNumber: cooperativeRegistrationNumber,
        registrationNumber13: normalizedRegistrationNumber,
        cooperativeName: business.title ?? ''
      };
    }

    this.restoreInitialSubdistrict(business, businessType);
    this.initialBusinessApplied = true;
    this.setBusinessType(businessType, false);
  }

  private restoreInitialSubdistrict(business: UserBusinessDto, type: BusinessType): void {
    const subdistrictId = type === 'individual'
      ? business.subdistrictId
      : (business.hqSubdistrictId ?? business.subdistrictId);
    if (subdistrictId == null) {
      this.subDistrict = null;
      this.districtName = undefined;
      this.provinceName = undefined;
      return;
    }

    const subdistrictZipcode = type === 'individual'
      ? business.zipcode
      : (business.hqZipcode ?? business.zipcode);

    this.businessApiService.getAddressBySubdistrictId(subdistrictId, subdistrictZipcode).subscribe({
      next: (option) => {
        // Keep API-driven form state outside the initializer's active change
        // detection pass; edit forms can be created while its @if block renders.
        setTimeout(() => {
          if (this.initialBusiness !== business) {
            return;
          }
          this.subDistrict = option;
          this.districtName = option.districtTitle ?? '';
          this.provinceName = option.provinceTitle ?? '';
          if (type === 'individual') {
            this.model.subdistrictId = option.subdistrictId ?? null;
            this.model.districtId = option.districtId ?? null;
            this.model.provinceId = option.provinceId ?? null;
            this.model.zipcode = option.zipcode ?? null;
          } else {
            this.model.hqSubdistrictId = option.subdistrictId ?? null;
            this.model.hqDistrictId = option.districtId ?? null;
            this.model.hqProvinceId = option.provinceId ?? null;
            this.model.hqZipcode = option.zipcode ?? null;
          }
          this.cdr.markForCheck();
        });
      },
      error: () => undefined
    });
  }

  private normalizeBusinessTypeIds(ids: number[] | null | undefined): number[] {
    if (!Array.isArray(ids)) {
      return [];
    }
    return Array.from(new Set(
      ids.map((id) => Number(id)).filter((id) => Number.isFinite(id))
    ));
  }

  protected buildBackendPayload(type: BusinessType): UserBusinessDto {
    const id = this.editMode ? this.initialBusiness?.id ?? null : null;
    const businessRegFile = this.initialBusiness?.businessRegFile ?? null;
    const selectedCertificateKeys = this.getSelectedCertificateKeys(this.selectedCertificateKeys);
    const common = {
      id,
      title: this.model.title ?? null,
      titleEn: this.model.titleEn ?? null,
      detail: this.model.detail ?? null,
      email: this.model.email ?? null,
      webSite: this.model.webSite ?? null,
      businessRegFile,
      certificateNos: this.getSelectedCertificateNos(selectedCertificateKeys),
      certificates: this.getSelectedcertificates(selectedCertificateKeys),
      businessTypeIds: this.normalizeBusinessTypeIds(this.selectedBusinessTypeIds),
      latitude: this.model.latitude ?? null,
      longitude: this.model.longitude ?? null,
      state: this.isActive ? 1 : 0
    };

    if (type === 'individual') {
      return {
        ...common,
        businessFormat: 1,
        taxId: this.model.taxId ?? null,
        savedBy: this.model.savedBy ?? null,
        addressNo: this.model.addressNo ?? null,
        building: null,
        floor: null,
        moo: null,
        soi: this.model.soi ?? null,
        street: this.model.street ?? null,
        subdistrictId: this.model.subdistrictId ?? null,
        districtId: this.model.districtId ?? null,
        provinceId: this.model.provinceId ?? null,
        zipcode: this.model.zipcode ?? null,
        phone: this.model.phone ?? null,
        fax: this.model.fax ?? null
      };
    }

    if (type === 'company') {
      return {
        ...common,
        businessFormat: 2,
        taxId: this.model.taxId ?? null,
        juristicType: this.model.juristicType ?? null,
        juristicRegDate: this.model.juristicRegDate ?? null,
        juristicName: this.model.juristicName || null,
        juristicAddress: this.model.juristicAddress || null,
        juristicAuthorizedSignatory: this.model.juristicAuthorizedSignatory || null,
        juristicDirectors: this.model.juristicDirectors || null,
        hqAddressNo: this.model.hqAddressNo ?? null,
        hqSoi: this.model.hqSoi ?? null,
        hqStreet: this.model.hqStreet ?? null,
        hqSubdistrictId: this.model.hqSubdistrictId ?? null,
        hqDistrictId: this.model.hqDistrictId ?? null,
        hqProvinceId: this.model.hqProvinceId ?? null,
        hqZipcode: this.model.hqZipcode ?? null,
        hqPhone: this.model.hqPhone ?? null,
        hqFax: this.model.hqFax ?? null,
        hqMobile: this.model.hqPhone ?? null
      };
    }

    return {
      ...common,
      businessFormat: 3,
      taxId: this.model.taxId ?? null,
      juristicType: this.cooperativeType(),
      juristicName: this.model.juristicName ?? null,
      hqAddressNo: this.model.hqAddressNo ?? null,
      hqSoi: this.model.hqSoi ?? null,
      hqStreet: this.model.hqStreet ?? null,
      hqSubdistrictId: this.model.hqSubdistrictId ?? null,
      hqDistrictId: this.model.hqDistrictId ?? null,
      hqProvinceId: this.model.hqProvinceId ?? null,
      hqZipcode: this.model.hqZipcode ?? null,
      hqPhone: this.model.hqPhone ?? null,
      hqFax: this.model.hqFax ?? null,
      hqMobile: this.model.hqPhone ?? null
    };
  }

  /**
   * Mirrors the certificate selection into the template-driven model field.
   *
   * The certificate picker writes its value into the shared `certificateNos`
   * control via ng-select's `[formControl]`. That path bypasses the sibling
   * `[ngModel]` (ng-select emits with emitViewToModelChange=false), so the
   * `(ngModelChange)` on the hidden input never fires and
   * `selectedCertificateKeys` would stay stale. A stale model means the
   * submit-time payload guard sees zero certificates and re-marks the control
   * invalid — i.e. "I selected a value but the field is still invalid".
   * `app-certificate-select` re-emits the selection through its
   * `selectionChange` output and every form wires that here.
   *
   * NOTE: this handler also fires while the initial business data loads
   * (applyInitialBusiness writes the restored selection into the control).
   * It must NOT markAsDirty/markAsTouched, or a pristine untouched form
   * immediately shows "กรุณาเลือกอย่างน้อย 1 ใบรับรอง". Touch state comes
   * from ng-select's own blur handling and from onSubmit()'s explicit
   * markAllAsTouched()/markAsTouched() calls.
   */
  onCertificateSelectionChange(keys: string[] | null | undefined): void {
    this.selectedCertificateKeys = Array.isArray(keys) ? keys : [];
    this.fillAddressFromSelectedCertificate();
    this.cdr.markForCheck();
  }

  /** Key of the certificate whose production site last filled the address, so a re-render does not re-fill. */
  private appliedSiteKey: string | null = null;

  /**
   * Work item 176: with a certificate selected, the address is the production site thaitraces
   * holds for it — the newest certificate's when several are selected. With none selected the
   * address stays whatever the form already had (a business may carry no certificate at all).
   * Runs on a selection change only, never on the restored selection of an existing business.
   */
  private fillAddressFromSelectedCertificate(): void {
    const newest = this.newestSelectedCertificateWithSite();
    if (!newest?.site) {
      this.appliedSiteKey = null;
      return;
    }
    const key = `${newest.certificateKey ?? ''}`.trim().toUpperCase();
    if (key === this.appliedSiteKey) {
      return;
    }
    this.appliedSiteKey = key;
    this.applyProductionSite(newest.site);
  }

  /** The selected certificate that carries a site and was issued last (start date, then end date, then number). */
  newestSelectedCertificateWithSite(): BusinessCertificateOptionDto | null {
    const candidates = this.getSelectedCertificateKeys(this.selectedCertificateKeys)
      .map((key) => this.findCertificateOptionByKey(key))
      .filter((item): item is BusinessCertificateOptionDto => !!item?.site);
    if (candidates.length === 0) {
      return null;
    }
    const text = (value: string | null | undefined): string => `${value ?? ''}`;
    return [...candidates].sort((left, right) =>
      text(right.startDate).localeCompare(text(left.startDate))
      || text(right.endDate).localeCompare(text(left.endDate))
      || text(right.certificateNo).localeCompare(text(left.certificateNo), undefined, {numeric: true})
    )[0];
  }

  /** Writes a production site into the address block of the active form (contact address for an individual, HQ otherwise). */
  private applyProductionSite(site: ProductionSiteDto): void {
    const individual = this.businessType() === 'individual';
    if (individual) {
      this.model.addressNo = site.addressNo ?? null;
      this.model.moo = site.moo ?? null;
      this.model.soi = site.soi ?? null;
      this.model.street = site.road ?? null;
    } else {
      this.model.hqAddressNo = site.addressNo ?? null;
      this.model.hqMoo = site.moo ?? null;
      this.model.hqSoi = site.soi ?? null;
      this.model.hqStreet = site.road ?? null;
    }
    if (site.latitude && site.longitude) {
      this.model.latitude = site.latitude;
      this.model.longitude = site.longitude;
    }
    const code = `${site.subdistrictCode ?? ''}`.trim();
    const names = {
      subdistrict: site.subdistrictName ?? undefined,
      district: site.districtName ?? undefined,
      province: site.provinceName ?? undefined,
      zipcode: site.postcode ?? undefined
    };
    if (!code && !names.subdistrict) {
      return;
    }
    // Guard against a form reset (type switch) while this lookup is in flight.
    const activeModel = this.model;
    // thaitraces carries the same 6-digit sub-district code as masterdata; a site without one is
    // matched on its names like a cooperative registry address.
    const lookup = code
      ? this.businessApiService.getAddressBySubdistrictCode(code, names)
      : this.businessApiService.searchAddresses(names.subdistrict!, 10).pipe(
        map((options) => options.find((candidate) =>
          this.sameAddressPart(candidate.subdistrictTitle, names.subdistrict)
          && (!names.district || this.sameAddressPart(candidate.districtTitle, names.district))
          && (!names.province || this.sameAddressPart(candidate.provinceTitle, names.province))
        ) ?? null));
    lookup.subscribe({
      next: (option) => {
        if (this.model !== activeModel || !option) {
          this.cdr.detectChanges();
          return;
        }
        this.onSubDistrictChange(option);
        if (!option.zipcode && site.postcode) {
          if (individual) {
            this.model.zipcode = site.postcode;
          } else {
            this.model.hqZipcode = site.postcode;
          }
        }
        this.cdr.detectChanges();
      },
      error: () => {
        if (this.model !== activeModel) {
          return;
        }
        // No masterdata match: keep the typed parts, leave the sub-district for the user.
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Called after the certificate-sync button finishes a ThaiTraces sync for
   * the business taxId: re-load the certificate options so the freshly synced
   * certificates are selectable. The current selection is kept.
   */
  onCertificatesSynced(): void {
    this.certificateOptions = [];
    this.loadCertificateOptions();
  }

  /**
   * A certificate the business keyed in itself (GMP, HACCP, …) is reloaded into the options and
   * selected straight away — it was added for this business, after all.
   */
  onCertificateAdded(item: CertificateItemDto): void {
    if (item?.id != null) {
      // Reloading the options re-applies the stored business (edit mode), which would drop the
      // selection made just now — so the key is merged in once the options are back.
      this.pendingCertificateKeys.push(`${(item.certificateType ?? 'SELF_CREATED').toUpperCase()}:${item.id}`);
    }
    this.onCertificatesSynced();
  }

  /** Keys to select as soon as the certificate options have been (re)loaded. */
  private pendingCertificateKeys: string[] = [];

  private loadCertificateOptions(): void {
    this.businessApiService.listCertificates().subscribe({
      next: (items) => {
        this.certificateOptions = (items ?? []).map((item) => ({
          ...item,
          displayLabel: this.formatCertificateLabel(item)
        }));
        this.initialBusinessApplied = false;
        this.applyInitialBusiness();
        if (this.pendingCertificateKeys.length > 0) {
          this.selectedCertificateKeys = Array.from(new Set([...this.selectedCertificateKeys, ...this.pendingCertificateKeys]));
          this.pendingCertificateKeys = [];
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.certificateOptions = [];
        this.pendingCertificateKeys = [];
        this.cdr.markForCheck();
      }
    });
  }

  getSelectedCertificateKeys(value: string[] | null | undefined): string[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return Array.from(new Set(value.map((item) => `${item ?? ''}`.trim()).filter((item) => !!item)));
  }

  getSelectedCertificateNos(value: string[] | null | undefined): string[] {
    const keys = this.getSelectedCertificateKeys(value);
    return Array.from(new Set(
      keys.map((key) => this.findCertificateOptionByKey(key)?.certificateNo?.trim() ?? '').filter((item) => !!item)
    ));
  }

  getSelectedcertificates(value: string[] | null | undefined): BusinessCertificateRefDto[] {
    const keys = this.getSelectedCertificateKeys(value);
    return keys
      .map((key) => this.findCertificateOptionByKey(key))
      .filter((item): item is BusinessCertificateOptionDto => !!item && !!item.id)
      .map((item) => ({
        certificateId: item.id,
        certificateType: item.certificateType ?? 'SELF_CREATED',
        certificateNo: item.certificateNo ?? '',
        farmId: item.farmId ?? null
      }));
  }

  private getInitialCertificateSelectionKeys(business: UserBusinessDto): string[] {
    const refs = Array.isArray(business.certificates) ? business.certificates : [];
    if (refs.length > 0) {
      const keys = refs
        .map((ref) => this.toCertificateKey(ref.certificateType, ref.certificateId))
        .filter((item): item is string => !!item);
      if (keys.length > 0) {
        return keys;
      }
    }
    const nos = Array.isArray(business.certificateNos) ? business.certificateNos : [];
    return nos
      .map((certificateNo) => this.findCertificateOptionByNo(certificateNo))
      .filter((item): item is BusinessCertificateOptionDto => !!item?.certificateKey)
      .map((item) => item.certificateKey as string);
  }

  private findCertificateOptionByKey(key: string): BusinessCertificateOptionDto | undefined {
    const normalizedKey = `${key ?? ''}`.trim().toUpperCase();
    return this.certificateOptions.find((item) => `${item.certificateKey ?? ''}`.trim().toUpperCase() === normalizedKey);
  }

  private findCertificateOptionByNo(certificateNo: string): BusinessCertificateOptionDto | undefined {
    const normalizedNo = `${certificateNo ?? ''}`.trim();
    if (!normalizedNo) {
      return undefined;
    }
    return this.certificateOptions.find((item) => `${item.certificateNo ?? ''}`.trim() === normalizedNo);
  }

  private toCertificateKey(type?: string | null, id?: number | null): string | null {
    const normalizedType = `${type ?? ''}`.trim().toUpperCase();
    if (!normalizedType || id == null) {
      return null;
    }
    return `${normalizedType}:${id}`;
  }

  formatCertificateLabel(certificate: BusinessCertificateOptionDto): string {
    const parts = [
      this.formatCertificateType(certificate.certificateType),
      `${certificate.title ?? ''}`.trim(),
      `${certificate.certificateNo ?? ''}`.trim(),
      `${certificate.ownerName ?? ''}`.trim(),
      // The production site too, so typing a sub-district finds the certificate (work item 177).
      formatProductionSite(certificate.site),
      certificate.id != null ? `#${certificate.id}` : ''
    ].filter((item) => !!item);
    return parts.length > 0 ? parts.join(' | ') : 'ยังไม่ระบุ';
  }

  private formatCertificateType(type?: string): string {
    const normalized = `${type ?? ''}`.trim().toUpperCase();
    if (normalized === 'LEGACY') {
      return '[ดึงมา]';
    }
    if (normalized === 'SELF_CREATED' || normalized === 'SELF') {
      return '[สร้างเอง]';
    }
    return normalized ? `[${normalized}]` : '';
  }
}
